import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_04: GgxTourDefinition = {
    key: '[HOWTO-04] Refining your prompt via Hillclimbing',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 5,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Refining Your Prompt with Hill Climbing',
        subtitle:
            'Reducing risk in one area often raises it in another. Hill climbing improves a ' +
            'prompt through small, controlled changes: make one targeted edit, measure it with ' +
            'objective reports, keep it only if results improve, then repeat. This tour runs a ' +
            'hill climb from start to finish.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You picked a completed base run, started a hill climb, refined a prompt, trimmed ' +
            'the evaluations down to what matters, and submitted the job. Compare the results ' +
            'against the base run to see the effect of your change, and remember to keep each ' +
            'change small so you can measure it.',
        learned: [
            'A **hill climb** starts from a completed base run.',
            'Change **one prompt at a time** so the effect is measurable.',
            '**Trim the evaluations** to the report you are testing, then compare.',
        ],
    },
    chapters: [
        {
            name: 'Open the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'map',
            steps: [2, 3, 4],
        },
        {
            name: 'Start a hill climb',
            blurb: 'Pick a base run and open a hill climb',
            icon: 'pulse',
            steps: [5, 6, 7],
        },
        {
            name: 'Refine the prompt',
            blurb: 'Choose a prompt and edit it',
            icon: 'search',
            steps: [8, 9, 10],
        },
        {
            name: 'Trim the evaluations',
            blurb: 'Keep the report you are testing',
            icon: 'list',
            steps: [11, 12, 13, 14, 15, 16, 17],
        },
        {
            name: 'Run the job',
            blurb: 'Submit and compare',
            icon: 'compass',
            steps: [18],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: "Let's open the pipeline we will improve",
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will use <strong>Combined Customer Intent and Response Pipeline</strong>, ' +
                'which already has a completed evaluation we can build on. ' +
                'The quickest way to it is the global search. It finds pipelines, prompts, ' +
                'reports and portals by name from any page, and <strong>Ctrl+K</strong> opens ' +
                'it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens, with its tabs across the top.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            target: '[test-id="tab-item-Jobs"]',
            scrollIntoView: true,
            title: 'Hill climbing starts from a past run',
            whatHappens: "the Jobs tab opens with the pipeline's past runs.",
            icon: 'clock',
            description:
                'Hill climbing means improving a prompt one small change at a time and measuring ' +
                'each change with the same test, so you know for sure whether it helped. That ' +
                'measuring happens in Jobs, where every evaluation of this pipeline is kept. ' +
                'Click <strong>Jobs</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'Jobs',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="pinned-job-Example Dashboard Run"]',
            title: 'Here is our starting point',
            whatHappens: "the run's dashboard opens with its scores and settings.",
            icon: 'grid',
            description:
                'A hill climb needs a base: a finished evaluation that used the current prompt. ' +
                'Its scores are what we will try to beat. <strong>Example Dashboard Run</strong> ' +
                'is pinned at the top and already complete, so it is perfect. Click it to open ' +
                'it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="hillclimb-button"]',
            title: "Let's start the hill climb",
            whatHappens: 'a new hill climb job opens, pre-filled from this run.',
            icon: 'pulse',
            description:
                "From a finished run, GGX can set up a hill climb for you. It copies the run's " +
                'data and reports, so your change is tested under exactly the same conditions as ' +
                'the original. Click <strong>Hillclimb</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="hillclimb-add-prompt-button"]',
            title: 'Which prompt are we improving?',
            whatHappens: 'a list of every prompt this pipeline uses opens.',
            icon: 'list',
            description:
                'A hill climb looks like a normal evaluation, with one extra section: Prompts. A ' +
                'pipeline can use several prompts, and here you choose the one you want to work ' +
                'on. Click <strong>Add prompt</strong> to see them.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'Add Prompt',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="hillclimb-add-prompt-option-Existing Customer Card Related SQL Generation Prompt (Constrained)"]',
            title: 'This one decides when to look up data',
            whatHappens: 'the prompt opens in an editor below, ready for your change.',
            icon: 'search',
            description:
                '<strong>Existing Customer Card Related SQL Generation Prompt ' +
                '(Constrained)</strong> tells the model when it needs to query the customer ' +
                'database and when it can answer from general knowledge. Getting that decision ' +
                'right matters: an unnecessary lookup slows the bot down and can return the ' +
                'wrong data. Click it to select it.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="hillclimb-prompt-definition-Existing Customer Card Related SQL Generation Prompt (Constrained)"]',
            title: 'Make one small, deliberate change',
            whatHappens:
                'your edited prompt is what the hill climb will test against the original.',
            icon: 'search',
            description:
                'The trick to hill climbing is changing one thing at a time, so you know what ' +
                'caused the result. Today we are teaching the prompt that general card ' +
                'questions, like how to activate a card or what the annual fee is, do not need a ' +
                'database lookup. Find the <strong>No Data Pull Required</strong> section in the ' +
                'editor, paste the line below at the end of it, then click ' +
                'Continue.<pre data-ggx-code data-ggx-wrap>General card questions such as activation, ' +
                'blocking, annual fees, and policies that are not tied to a specific account do ' +
                'not require a data pull.</pre>',
            side: 'top',
            align: 'start',
        },
        {
            target: '[test-id="eval-header-0"]',
            title: 'The reports came along for free',
            whatHappens: 'the next steps remove the reports we do not need.',
            icon: 'grid',
            description:
                'GGX carried over the reports and the test data from the base run. That matters: ' +
                'if the test changed, you could not tell whether a better score came from your ' +
                'prompt or from easier questions. We will keep <strong>Response Accuracy (Credit ' +
                'Card Queries)</strong>, since accuracy is what our change should improve, and ' +
                'remove the other six to keep the run fast. Click Continue.',
            side: 'top',
            align: 'start',
        },
        {
            target: '[test-id="eval-delete-1"]',
            title: "Let's remove the ones we do not need",
            whatHappens: 'the report disappears from the run.',
            icon: 'list',
            description:
                'Each extra report pulls its own data and adds minutes to the run. Since we only ' +
                'care about accuracy today, we will remove the rest one by one. First up is ' +
                '<strong>Response Accuracy (Non Credit-Card Queries)</strong>, which scores ' +
                'questions outside the credit card topic. Click its delete icon.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-delete-2"]',
            title: 'Faithfulness can wait',
            whatHappens: 'the report disappears from the run.',
            icon: 'list',
            description:
                '<strong>Response Faithfulness (Credit Card Queries)</strong> checks whether the ' +
                "bot's answers stick to what the database actually returned. A good report, but " +
                'not what our change is about. Click its delete icon.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'the delete icon',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-delete-3"]',
            title: 'Stability, part one',
            whatHappens: 'the report disappears from the run.',
            icon: 'list',
            description:
                '<strong>Response Stability Repeated Utterance</strong> asks the same question ' +
                'several times to see whether the bot stays consistent. Not today. Click its ' +
                'delete icon.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'the delete icon',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-delete-4"]',
            title: 'Stability, part two',
            whatHappens: 'the report disappears from the run.',
            icon: 'list',
            description:
                '<strong>Response Stability Perturbed Utterance</strong> rewords each question ' +
                'slightly to check the bot is not thrown off by phrasing. Also not needed for ' +
                'this test. Click its delete icon.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'the delete icon',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-delete-5"]',
            title: 'Toxicity is not our focus today',
            whatHappens: 'the report disappears from the run.',
            icon: 'list',
            description:
                '<strong>Toxicity Report</strong> watches for unsafe or offensive language in ' +
                'the replies. It matters before anything goes live, but it will not tell us ' +
                'whether our prompt change worked. Click its delete icon.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'the delete icon',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-delete-6"]',
            title: 'Last one',
            whatHappens: 'only Response Accuracy (Credit Card Queries) is left in the run.',
            icon: 'list',
            description:
                '<strong>Vulnerability Report</strong> tries jailbreak attempts against the bot ' +
                'to see if it can be tricked. Remove it and only Response Accuracy remains, ' +
                'which is exactly what we want. Click its delete icon.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'the delete icon',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="run-new-job"]',
            title: "One change, one report. Let's run it",
            whatHappens:
                'the job starts in the background; small datasets finish in about 5 to 10 ' +
                'minutes.',
            icon: 'compass',
            description:
                'This is a clean experiment: the same data and the same report as the base run, ' +
                'with one prompt change. When it finishes, compare its accuracy score with the ' +
                'base run. If it improved, keep the change and make the next one. If not, undo ' +
                'it and try something else. Click <strong>Run New Job</strong>.',
            side: 'left',
            align: 'center',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
    ],
};
